<template>
  <nav class="bottom-nav" role="navigation" aria-label="Mobile navigation">
    <router-link
      v-for="item in navItems"
      :key="item.to"
      :to="item.to"
      class="bn-item"
      :class="{ active: isActive(item.to) }"
    >
      <span class="bn-icon"><AppIcon :name="item.icon" :size="20" /></span>
      <span class="bn-label">{{ item.label }}</span>
    </router-link>
    <button
      class="bn-item"
      :class="{ active: moreOpen }"
      @click="toggleMore"
      aria-label="More options"
    >
      <span class="bn-icon"><AppIcon name="nav-more" :size="20" /></span>
      <span class="bn-label">{{ t('nav.more') }}</span>
    </button>

    <Transition name="sheet">
      <div v-if="moreOpen" class="bottom-sheet-overlay" @click="closeMore">
        <div class="bottom-sheet" @click.stop>
          <div class="bottom-sheet-handle"></div>
          <router-link
            v-for="item in moreItems"
            :key="item.to"
            :to="item.to"
            class="sheet-item"
            :class="{ active: isActive(item.to) }"
            @click="closeMore"
          >
            <span class="sheet-icon"><AppIcon :name="item.icon" :size="20" /></span>
            <span class="sheet-label">{{ item.label }}</span>
          </router-link>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'

const route = useRoute()
const { t } = useI18n()
const moreOpen = ref(false)

function isActive(path: string): boolean {
  return route.path === path || route.path.startsWith(path + '/')
}

function toggleMore() {
  moreOpen.value = !moreOpen.value
}

function closeMore() {
  moreOpen.value = false
}

const navItems = computed(() => [
  { icon: 'nav-home', label: t('nav.lobby'), to: '/player/lobby' },
  { icon: 'nav-games', label: t('nav.games'), to: '/player/games' },
  { icon: 'nav-wallet', label: t('nav.wallet'), to: '/player/wallet' },
  { icon: 'nav-user', label: t('nav.profile'), to: '/player/profile' },
])

const moreItems = computed(() => [
  { icon: 'nav-promotions', label: t('nav.promotions'), to: '/player/bonuses' },
  { icon: 'nav-vip', label: t('nav.vip'), to: '/player/vip' },
  { icon: 'nav-leaderboard', label: t('nav.leaderboard'), to: '/player/leaderboard' },
  { icon: 'nav-affiliates', label: t('nav.affiliates'), to: '/player/affiliates' },
  { icon: 'nav-history', label: t('nav.history'), to: '/player/history' },
])
</script>

<style scoped>
.bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--z-sticky, 200);
  height: var(--bottom-nav-height, 56px);
  padding-bottom: env(safe-area-inset-bottom, 0);
  background: var(--bg-header, #0f1923);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  align-items: center;
}

.bn-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: var(--space-1, 4px);
  color: var(--text-tertiary, #7f8fa4);
  text-decoration: none;
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: none;
  transition: color var(--transition-fast, 150ms ease);
  height: 100%;
}

.bn-item.active {
  color: var(--accent-primary, #00e701);
}

.bn-icon {
  font-size: 20px;
  line-height: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bn-label {
  line-height: 1;
  letter-spacing: 0.02em;
}

/* Bottom sheet overlay */
.bottom-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal, 400);
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
}

.bottom-sheet {
  width: 100%;
  background: var(--bg-secondary, #1a2c38);
  border-top-left-radius: var(--radius-xl, 16px);
  border-top-right-radius: var(--radius-xl, 16px);
  padding: 8px 16px calc(var(--bottom-nav-height, 56px) + env(safe-area-inset-bottom, 0) + 8px);
}

.bottom-sheet-handle {
  width: 32px;
  height: 4px;
  background: var(--text-tertiary, #7f8fa4);
  border-radius: var(--radius-full, 9999px);
  margin: 4px auto 12px;
  opacity: 0.5;
}

.sheet-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  color: var(--text-secondary, #b1bad3);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  border-radius: var(--radius-md, 8px);
  transition: all var(--transition-fast, 150ms ease);
}

.sheet-item:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.sheet-item.active {
  color: var(--accent-primary, #00e701);
}

.sheet-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}

.sheet-label {
  flex: 1;
}

/* Bottom sheet transitions */
.sheet-enter-active {
  transition: opacity 200ms ease;
}

.sheet-enter-active .bottom-sheet {
  animation: sheetSlideUp 250ms ease forwards;
}

.sheet-leave-active {
  transition: opacity 150ms ease;
}

.sheet-leave-active .bottom-sheet {
  animation: sheetSlideDown 200ms ease forwards;
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

@keyframes sheetSlideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes sheetSlideDown {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

/* Show only on mobile */
@media (max-width: 767px) {
  .bottom-nav {
    display: flex;
  }
}
</style>
